/**
 * Typed bindings for /api/channels.
 *
 * Sprint 7 ships:
 *   - Channel list CRUD (manual + bulk)
 *   - Blacklist / whitelist
 *   - Telemetr.me API key storage and test stub
 * Sprint 8 will add a `useScrape()` hook on top of this.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "./api";

export type ChannelEntityType =
  | "broadcast_channel"
  | "megagroup"
  | "chat"
  | "discussion_group"
  | "unknown";

export type ChannelSource =
  | "telemetr_api"
  | "csv_import"
  | "manual_seed"
  | "linked_discussion";

export type ChannelParsingStatus =
  | "queued"
  | "parsing"
  | "done"
  | "skipped"
  | "skipped_no_users"
  | "failed"
  | "failed_private"
  | "pending_join"
  | "deferred";

export interface ChannelSummary {
  id: string;
  tg_channel_id: number | null;
  username: string | null;
  /** Set for channels added via a private invite link (t.me/+HASH). */
  invite_hash: string | null;
  title: string | null;
  entity_type: ChannelEntityType;
  description: string | null;
  subscribers: number | null;
  language: string | null;
  last_post_at: string | null;
  is_active: boolean | null;
  members_visible: boolean | null;
  linked_discussion_id: number | null;
  parent_channel_id: string | null;
  source: ChannelSource;
  external_id: string | null;
  parsing_status: ChannelParsingStatus;
  last_scraped_at: string | null;
  last_scraped_message_id: number | null;
  /** Set while parsing_status is "pending_join" — when we asked to join. */
  join_requested_at: string | null;
  notes: string | null;
  /** Why the newest parse ended as it did (from the latest ScrapeJob). */
  last_error: string | null;
  created_at: string;
  updated_at: string;
}

export interface BulkImportResult {
  inserted: number;
  skipped_duplicates: number;
  /** Lines with no channel on them at all — headings, notes, prose. */
  skipped_noise: number;
  invalid_lines: { line_number: number; raw: string; reason: string }[];
}

export type ChannelListType = "blacklist" | "whitelist";
export type ChannelMatchType = "exact_username" | "tg_channel_id" | "title_regex";

export interface ChannelListEntry {
  id: string;
  list_type: ChannelListType;
  match_type: ChannelMatchType;
  match_value: string;
  reason: string | null;
  added_by_user_id: string | null;
  created_at: string;
}

export interface TelemetrCredentials {
  has_key: boolean;
  api_key_label: string | null;
  last_test_at: string | null;
  last_test_ok: boolean | null;
  last_test_error: string | null;
  rate_limit_per_minute: number;
}

export interface TelemetrTestResult {
  ok: boolean;
  error: string | null;
  status?: string | null;
  requests_used?: number | null;
  requests_limit?: number | null;
  channels_used?: number | null;
  channels_limit?: number | null;
  billing_end?: string | null;
}

export interface TelemetrUsage {
  status: string;
  requests_used: number;
  requests_limit: number;
  channels_used: number;
  channels_limit: number;
  search_requests_used: number;
  search_requests_limit: number;
  billing_end: string | null;
}

export interface TelemetrImportRequest {
  term?: string;
  category?: string;
  language?: string;
  country?: string;
  min_subscribers?: number;
  max_subscribers?: number;
  min_views?: number;
  min_er?: number;
  verified_only?: boolean;
  exclude_private?: boolean;
  peer_type?: "Channel" | "Group";
  sort_by?: string;
  sort_direction?: "asc" | "desc";
  limit?: number;
  skip?: number;
}

export interface TelemetrImportResult {
  fetched: number;
  inserted: number;
  skipped_duplicates: number;
  total_in_catalog: number;
}

export const CHANNELS_KEY = ["channels"] as const;
export const CHANNEL_LIST_KEY = ["channel_lists"] as const;
export const TELEMETR_KEY = ["telemetr_credentials"] as const;

export function useChannels() {
  return useQuery<ChannelSummary[]>({
    queryKey: CHANNELS_KEY,
    queryFn: () => api.get<ChannelSummary[]>("/api/channels"),
  });
}

export function useCreateChannel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: {
      username?: string;
      tg_channel_id?: number;
      title?: string;
      notes?: string;
    }) => api.post<ChannelSummary>("/api/channels", payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: CHANNELS_KEY }),
  });
}

export function useBulkImportChannels() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: { text: string; format?: string; delimiter?: string }) =>
      api.post<BulkImportResult>("/api/channels/bulk", payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: CHANNELS_KEY }),
  });
}

export function useUpdateChannel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      ...payload
    }: {
      id: string;
      title?: string;
      notes?: string;
      parsing_status?: ChannelParsingStatus;
    }) => api.patch<ChannelSummary>(`/api/channels/${id}`, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: CHANNELS_KEY }),
  });
}

export function useDeleteChannel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete<void>(`/api/channels/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: CHANNELS_KEY }),
  });
}

export function useChannelLists() {
  return useQuery<ChannelListEntry[]>({
    queryKey: CHANNEL_LIST_KEY,
    queryFn: () => api.get<ChannelListEntry[]>("/api/channels/lists/all"),
  });
}

export function useCreateListEntry() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: {
      list_type: ChannelListType;
      match_type: ChannelMatchType;
      match_value: string;
      reason?: string;
    }) => api.post<ChannelListEntry>("/api/channels/lists", payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: CHANNEL_LIST_KEY }),
  });
}

export function useUpdateListEntry() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      api.patch<ChannelListEntry>(`/api/channels/lists/${id}`, { reason }),
    onSuccess: () => qc.invalidateQueries({ queryKey: CHANNEL_LIST_KEY }),
  });
}

export function useDeleteListEntry() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete<void>(`/api/channels/lists/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: CHANNEL_LIST_KEY }),
  });
}

export function useTelemetrCredentials() {
  return useQuery<TelemetrCredentials>({
    queryKey: TELEMETR_KEY,
    queryFn: () =>
      api.get<TelemetrCredentials>("/api/channels/telemetr/credentials"),
  });
}

export function useSetTelemetrCredentials() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: {
      api_key: string;
      api_key_label?: string;
      rate_limit_per_minute?: number;
    }) => api.put<TelemetrCredentials>("/api/channels/telemetr/credentials", payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: TELEMETR_KEY }),
  });
}

export function useDeleteTelemetrCredentials() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.delete<void>("/api/channels/telemetr/credentials"),
    onSuccess: () => qc.invalidateQueries({ queryKey: TELEMETR_KEY }),
  });
}

export function useTestTelemetrCredentials() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api.post<TelemetrTestResult>("/api/channels/telemetr/test"),
    onSuccess: () => qc.invalidateQueries({ queryKey: TELEMETR_KEY }),
  });
}

export function useTelemetrUsage(enabled: boolean = false) {
  return useQuery<TelemetrUsage>({
    queryKey: ["telemetr_usage"],
    queryFn: () => api.get<TelemetrUsage>("/api/channels/telemetr/usage"),
    enabled,
  });
}

export function useTelemetrImport() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: TelemetrImportRequest) =>
      api.post<TelemetrImportResult>("/api/channels/telemetr/import", payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: CHANNELS_KEY });
      qc.invalidateQueries({ queryKey: ["telemetr_usage"] });
    },
  });
}

export interface ExpandSimilarResult {
  seeds_used: number;
  found: number;
  added: number;
  depth: number;
}

export function useExpandSimilar() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: { channel_ids?: string[]; depth?: number; max_per_seed?: number }) =>
      api.post<ExpandSimilarResult>("/api/channels/expand-similar", payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: CHANNELS_KEY }),
  });
}
